/* eslint-disable no-nested-ternary */
/* eslint-disable react/no-array-index-key */
import React, { useContext, useState } from 'react';
import moment from 'moment';
import Button from '@material-ui/core/Button';
import { ConfirmationDialog, Loader } from '@mummssoftware/common-ui';
import { Discharged } from '@mummssoftware/utils/constants';
import {
  GlobalContext,
  SET_LEVEL_OF_CARE,
  attemptedVisit,
} from '@mummssoftware/global-context/GlobalContext';
import {
  OPEN_VISIT_WIDGET,
  SET_PREP_VISIT,
  SET_ACTING_AS_ROLE,
  SET_IPU_VISIT,
  SET_IPU_RADIO_CHOICE,
} from '../Context/constants';
import { Context } from '../../context';
import { VisitContext } from '../Context/contexts/VisitContext';
import { VisitSubmitDiv, VisitButtonDiv } from '../../components';
import { MenuWrapper } from '../../components/layout/WrapperDiv';
import defaults from '../../translations/en.json';
import { useStyles } from './style';

export const VisitSubmitButtons = ({
  onStartVisit,
  isDisabled,
  isIPUDisabled,
  visitDate,
}: any) => {
  const classes = useStyles();
  const globalContext = useContext(GlobalContext);
  const { levelOfCare } = useContext(GlobalContext);
  const {
    dispatchVisit,
    checkforOtherUserHopeVisits,
    IPUVisit,
    ipuRadioChoice,
  } = useContext(VisitContext);
  const { patient } = useContext(Context);

  const isAttemptedVisit = levelOfCare === attemptedVisit;
  const isAttemptedVisitDisabled = !ipuRadioChoice;
  const [isOpen, setIsOpen] = useState(false);
  const openWarningDialog = () => setIsOpen(true);
  const [visitSubmitted, setVisitSubmitted] = useState(false);
  const startTitle = isAttemptedVisit
    ? defaults.recordVisit
    : IPUVisit
    ? defaults.startIPUVisit
    : defaults.startNewVisit;
  const startDisabled = isAttemptedVisit
    ? isAttemptedVisitDisabled
    : IPUVisit
    ? isIPUDisabled
    : isDisabled;
  const doNotShowPrep = IPUVisit || isAttemptedVisit;

  // show warning for DA patients
  const isPatientDA = patient?.currentStatus === Discharged;
  let patientDAWarning = false;
  if (isPatientDA) {
    // visit date should be on or before day of discharge
    const dischargedDate = moment(patient?.dischargeDate);
    patientDAWarning = moment(visitDate).isAfter(dischargedDate, 'day');
  }
  const onSubmitPrepVisit = () => {
    dispatchVisit({
      type: SET_PREP_VISIT,
      payload: true,
    });
  };

  const startVisit = async () => {
    setVisitSubmitted(true);
    await checkforOtherUserHopeVisits();
    const created = await onStartVisit();
    if (!created) {
      setVisitSubmitted(false);
      setIsOpen(false);
    }
  };

  /**
   * OP-33239: an IPU visit always starts at the current date, so the visit-date
   * confirmation is skipped. The discharged-patient warning still applies.
   */
  const startIPUVisit = () =>
    patientDAWarning ? openWarningDialog() : startVisit();

  const onCancel = () => {
    if (IPUVisit) {
      setTimeout(() => {
        dispatchVisit({
          type: SET_IPU_VISIT,
          payload: false,
        });
        dispatchVisit({
          type: SET_IPU_RADIO_CHOICE,
          payload: null,
        });
      }, 200);
    } else {
      dispatchVisit({
        type: SET_ACTING_AS_ROLE,
        payload: null,
      });
      globalContext.dispatch({
        type: SET_LEVEL_OF_CARE,
        payload: null,
      });
    }
    dispatchVisit({
      type: OPEN_VISIT_WIDGET,
      payload: false,
    });
  };

  const getDAPatientContent = () => (
    <div className={classes.dialogContent} id="da-patient-warning">
      <p className={classes.warningStyle}>{defaults.warningText}</p>
      <p className={classes.dialogMessage}>{defaults.dischargedPatient}</p>

      <div className={classes.menuStyle}>
        <Button
          className={classes.menuButton}
          size="small"
          variant="contained"
          color="default"
          onClick={() => setIsOpen(false)}
          disabled={visitSubmitted}
        >
          {defaults.changeVisitDate}
        </Button>
        <Button
          className={classes.menuButton}
          size="small"
          variant="contained"
          color="default"
          onClick={onCancel}
          disabled={visitSubmitted}
        >
          {defaults.cancel}
        </Button>
      </div>
    </div>
  );
  const getStartVisitContent = () => (
    <div
      className={`${classes.dialogContent} ${classes.startVisitContent}`}
      id="visit-submit-button"
    >
      <div className={classes.menuStyle}>
        <Button
          className={classes.menuButton}
          size="small"
          variant="contained"
          color="primary"
          onClick={() => setIsOpen(false)}
          disabled={visitSubmitted}
        >
          {defaults.changeDate.toUpperCase()}
        </Button>
        <Button
          className={classes.menuButton}
          size="small"
          variant="contained"
          color="primary"
          onClick={startVisit}
          disabled={visitSubmitted}
        >
          {defaults.startVisit.toUpperCase()}
        </Button>
      </div>
    </div>
  );

  const getMenuContent = () => {
    if (isOpen) {
      if (patientDAWarning) {
        return getDAPatientContent();
      }
      return getStartVisitContent();
    }
    return <></>;
  };

  const startOnLabel = IPUVisit
    ? defaults.startIPUVisitOn
    : defaults.startNewVisitOn;
  const dialogTitle = patientDAWarning
    ? startTitle
    : `${startOnLabel} ${moment(visitDate).format('MM/DD/YY')}`;

  return (
    <VisitSubmitDiv id="visit-submit-div">
      <VisitButtonDiv id="visit-button-div">
        <Button
          size="small"
          variant="contained"
          color="default"
          onClick={onCancel}
        >
          {defaults.cancel}
        </Button>

        {doNotShowPrep ? (
          <></>
        ) : (
          <Button
            disabled
            size="small"
            variant="contained"
            color="primary"
            onClick={onSubmitPrepVisit}
          >
            {defaults.prepOffline}
          </Button>
        )}

        <Button
          disabled={startDisabled}
          size="small"
          variant="contained"
          color="primary"
          onClick={
            isAttemptedVisit
              ? onStartVisit
              : IPUVisit
              ? startIPUVisit
              : openWarningDialog
          }
        >
          {startTitle}
        </Button>
      </VisitButtonDiv>
      {/* IPU visits start without the confirmation dialog; show the loader inline */}
      {visitSubmitted && !isOpen && <Loader />}
      {/* Confirmation Dialog for Start Visit */}
      <ConfirmationDialog
        open={isOpen}
        title={dialogTitle}
        fullWidth
        disableBackdropClick
        maxWidth="sm"
        handleClick={getMenuContent}
        noIcon
      >
        {visitSubmitted && <Loader />}
        <MenuWrapper id="menu-wrapper-visit">{getMenuContent()}</MenuWrapper>
      </ConfirmationDialog>
    </VisitSubmitDiv>
  );
};
